/**
 * The one reader for a streamed body of newline-delimited JSON (MAR-948) — NDJSON from the chat, run,
 * sheet, tool-playground and docproc streams, and `data:`-framed server-sent events from the public
 * research stream. Both sides use it, so it lives in `shared/`; it needs nothing but the WHATWG
 * streams and `TextDecoder`, which the browser and Node both provide.
 *
 * It replaced six hand-rolled copies of the same buffer/split/parse loop, which disagreed on two
 * things it now settles for everyone:
 *   - a final line with no trailing newline IS read (only docproc's copy did; the other five dropped
 *     a last event whose writer did not end it with `\n`);
 *   - an SSE `data:` prefix is stripped only when the caller says the body is SSE (`sse: true`).
 *
 * What a caller still decides: blank and malformed lines are skipped (a torn line is never worth
 * failing a stream over), while an error the HANDLER throws ends the read and reaches the caller — a
 * `failed` event that should abort the run can simply throw. A caller that wants to keep reading past
 * its own handler's error catches inside the handler. The guard is `tests/unit/jsonLinesGuard.test.ts`.
 */

export interface ReadJsonLinesOptions {
  /** Checked before each read: once aborted, the read stops at the next chunk boundary. */
  signal?: AbortSignal
  /** The body is server-sent events: strip a leading `data:` from each line before parsing. */
  sse?: boolean
}

function parseLine(raw: string, sse: boolean): { ok: true, value: unknown } | { ok: false } {
  let line = raw.trim()
  if (sse && line.startsWith('data:')) line = line.slice(5).trim()
  if (!line) return { ok: false }
  try {
    return { ok: true, value: JSON.parse(line) }
  } catch {
    return { ok: false }
  }
}

/**
 * Read `body` line by line, calling `onValue` with each parsed JSON value in order. An async
 * `onValue` is awaited before the next line is handled.
 */
export async function readJsonLines(
  body: ReadableStream<Uint8Array> | ReadableStreamDefaultReader<Uint8Array>,
  onValue: (value: unknown) => void | Promise<void>,
  options: ReadJsonLinesOptions = {},
): Promise<void> {
  const reader = 'getReader' in body ? body.getReader() : body
  const sse = options.sse === true
  const decoder = new TextDecoder()
  let buffer = ''

  const emit = async (raw: string) => {
    const parsed = parseLine(raw, sse)
    if (parsed.ok) await onValue(parsed.value)
  }

  while (true) {
    if (options.signal?.aborted) return
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newline: number
    while ((newline = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newline)
      buffer = buffer.slice(newline + 1)
      await emit(line)
    }
  }
  buffer += decoder.decode()
  await emit(buffer)
}
